import React, { useMemo } from 'react';
import { CatmullRomCurve3, Vector3 } from 'three';

import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { angleEquals, convertDegreesToRadians, Direction2D, Point2D } from '@paragon/geometry-library';
import { RenderOrder } from '@paragon/three-shared';

import { LabelVisual } from '../util/LabelVisual';
import { useDimensionStyle } from './dimensionStyles';
import { DistanceDimension } from './DistanceDimension';

const GUIDE_LINE_THICKNESS = 0.1;
const ARC_STEPS_PER_RADIAN = 20;

export interface AngleDimensionProps {
  readonly basePoint: Point2DInterface;
  readonly baseLineEndPoint: Point2DInterface;
  readonly endLineEndPoint: Point2DInterface;
}

export function AngleDimension(props: AngleDimensionProps) {
  const { labelMaterial, labelOptions } = useDimensionStyle('userCreated');
  const basePoint = Point2D.fromObject(props.basePoint);
  const baseLineEndPoint = Point2D.fromObject(props.baseLineEndPoint);
  const endLineEndPoint = Point2D.fromObject(props.endLineEndPoint);

  const angleInDegrees = Direction2D.fromPoints(basePoint, endLineEndPoint).angleTo(
    Direction2D.fromPoints(basePoint, baseLineEndPoint),
  );

  const arcRadiusX = baseLineEndPoint.x - basePoint.x;
  const arcRadiusY = baseLineEndPoint.y - basePoint.y;
  const arcCurve = useMemo(
    () => buildArcCurve(arcRadiusX, arcRadiusY, angleInDegrees),
    [arcRadiusX, arcRadiusY, angleInDegrees],
  );

  const absoluteAngleInDegrees = Math.abs(angleInDegrees);
  if (angleEquals(absoluteAngleInDegrees, 90) || angleEquals(absoluteAngleInDegrees, 0)) return null;

  const degreeSymbol = '°';
  const angleString = absoluteAngleInDegrees.toFixed(1).toString() + degreeSymbol;

  return (
    <object3D>
      <DistanceDimension
        styleVariant="userCreated"
        thickness={GUIDE_LINE_THICKNESS}
        segment={{ start: basePoint, end: baseLineEndPoint }}
      />
      <DistanceDimension
        styleVariant="userCreated"
        thickness={GUIDE_LINE_THICKNESS}
        segment={{ start: basePoint, end: endLineEndPoint }}
      />
      {arcCurve ? (
        <>
          <mesh position={basePoint.to3D().toArray()} material={labelMaterial}>
            <tubeGeometry args={[arcCurve, 10, 0.1, 6]} />
          </mesh>
          <LabelVisual
            {...labelOptions}
            renderOrder={RenderOrder.GlyphVisual_StandardDimension}
            fontSize={1}
            position={basePoint.add(baseLineEndPoint).multiplyScalar(0.5).add({ x: 0, y: 0.1 }).to3D().toArray()}
          >
            {angleString}
          </LabelVisual>
        </>
      ) : null}
    </object3D>
  );
}

function buildArcCurve(radiusX: number, radiusY: number, angleInDegrees: number) {
  const angleInRadians = convertDegreesToRadians(angleInDegrees);
  const stepCount = Math.abs(angleInRadians) * ARC_STEPS_PER_RADIAN;
  const angleAmount = angleInRadians / stepCount;
  const radius = new Vector3(radiusX, radiusY, 0);

  const arcPoints: Vector3[] = [];
  for (let i = 0; i < stepCount; i += 1) arcPoints.push(getPointOnCircle(radius, angleAmount * i));

  return arcPoints.length > 1 ? new CatmullRomCurve3(arcPoints) : null;
}

function getPointOnCircle(center: Vector3, angle: number) {
  return new Vector3(
    center.x * Math.cos(angle) + center.y * Math.sin(angle),
    -center.x * Math.sin(angle) + center.y * Math.cos(angle),
    0,
  );
}
